'use client'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'
import ItineraryHeader from './ItineraryHeader'

const ItineraryHeaderWrapper = ({ pageTitle, onBackClick }: { pageTitle: string; onBackClick: () => void }) => {
    const itineraryId = useGetItineraryId()
    const t = useTranslations('common')
    const locale = useLocale()
    const { formatDate } = useFormatDate()

    // Optimized selector - only get the data we actually use
    const { to, from, departureDate, arrivalDate, adults, children, infants, isError } = useGetItineraryDataQuery(
        {
            itineraryId
        },
        {
            selectFromResult: ({ data, isError }) => ({
                to: data?.searchCriteria.to || '',
                from: data?.searchCriteria.from || '',
                departureDate: data?.searchCriteria.departDate || '',
                arrivalDate: data?.searchCriteria.arrivalDate || '',
                adults: data?.searchCriteria?.paxInfo.adults || 0,
                children: data?.searchCriteria?.paxInfo.children || 0,
                infants: data?.searchCriteria?.paxInfo.infants || 0,
                isError
            })
        }
    )

    const route = useMemo(() => {
        const routeObject =
            locale === 'en'
                ? {
                      fromCity: from,
                      toCity: to
                  }
                : {
                      fromCity: to,
                      toCity: from
                  }
        return from && to ? t('segmentHeading', routeObject) : ''
    }, [from, to])

    const travellers = useMemo(() => {
        const paxCount = adults + children + infants
        return paxCount > 1
            ? t('multiTravellers', { travellers: paxCount })
            : t('singleTraveller', { travellers: paxCount })
    }, [adults, children, infants, t])

    const formattedDepartureDate = useMemo(
        () => (departureDate ? formatDate(departureDate, 'D MMM') : ''),
        [departureDate]
    )
    const formattedArrivalDate = useMemo(() => (arrivalDate ? formatDate(arrivalDate, 'D MMM') : ''), [arrivalDate])

    return (
        <ItineraryHeader
            onBackClick={onBackClick}
            pageTitle={pageTitle}
            route={route}
            departureDate={formattedDepartureDate}
            arrivalDate={formattedArrivalDate}
            travellers={travellers}
            isError={isError}
            isRTL={locale === 'ar'}
        />
    )
}

export default ItineraryHeaderWrapper
